import axios, { AxiosResponse } from "axios";
import {
  ProductListRes,
  ProductType,
  SingleProductRes,
  SingleProductWithBasesRes
} from "@/models/products/product";
import { PriceRes, PriceTotal } from "@/models/pricing/price";
import { Collection } from "@/models/products/collection";
import {
  APIError,
  AuthError,
  BadRequest,
  NotFoundError,
  PermissionsError
} from "./error_service";
import { UmbrellaData } from "@/mixins/umbrella";
import { AppHeaders } from "@/models/api/api";
import {
  ConfigFile,
  QuickshipConfigFile
} from "@/models/configurator/config_file";
import {
  CustomUmbrella,
  CustomUmbrellaOrder
} from "@/models/configurator/custom_umbrella";
import { FinishHandle } from "@/models/products/finish";
import { Stem } from "@/models/configurator/configurator_options";
import { BaseModel } from "@/models/products/base";

axios.defaults.headers = { apikey: process.env.VUE_APP_API_KEY } as AppHeaders;
axios.defaults.baseURL = process.env.VUE_APP_API_NODE_URL;

function removeSurcharge(price: number): number {
  const surcharge = parseFloat(process.env.VUE_APP_PRICE_SURCHARGE_VALUE_NUMBER || "");
  if (isNaN(surcharge) || surcharge <= 0) return price;
  return parseFloat((price / (1 + surcharge)).toFixed(2));
}

/**
 * Creates a new Product Service

 * @example
 * const productervice = new ProductService();
 * productService.getUmbrellas();
 */

export default class ProductService {
  public async getSingleProduct(
    model: string,
    bases?: boolean
  ): Promise<SingleProductRes | SingleProductWithBasesRes> {
    let product;
    let url = "/products/" + encodeURIComponent(model);
    if (bases) {
      url += "?bases";
    }
    try {
      const res: AxiosResponse = await axios.get(url);
      product = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return product;
  }

  public async getProductLine(handle: string): Promise<Collection> {
    let product;
    const url = "/productlines/collection/" + handle;
    try {
      const res: AxiosResponse = await axios.get(url);
      product = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("Product not found.", err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return product;
  }

  public async getProducts(optionsObject?: {
    category?: ProductType;
    pl?: string;
    sort?: "productline";
  }): Promise<Collection[] | any[]> {
    let product;
    let url = "/products";
    if (optionsObject) {
      let counter = 0;
      let char = "?";
      if (optionsObject.category) {
        counter++;
        if (counter > 1) {
          char = "&";
        }
        url += char + "category=" + optionsObject.category;
      }
      if (optionsObject.pl) {
        counter++;
        if (counter > 1) {
          char = "&";
        }
        url += char + "pl=" + optionsObject.pl;
      }
      if (optionsObject.sort) {
        counter++;
        if (counter > 1) {
          char = "&";
        }
        url += char + "sort=" + optionsObject.sort;
      }
    }
    try {
      const res: AxiosResponse = await axios.get(url);
      product = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        throw new NotFoundError("No products found.", err.response.status);
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return product;
  }

  public async getUmbrellaPrice(body: {
    model: string;
    item_details: {
      canopy: CustomUmbrellaOrder["canopy"];
      frame: CustomUmbrellaOrder["frame"];
      layout: CustomUmbrellaOrder["layout"];
    };
  }): Promise<PriceTotal> {
    let price;
    const url = "/prices/umbrella";
    try {
      const res: AxiosResponse = await axios.post(url, body);
      price = res.data.data;
      if (price.umbrella) {
        price.umbrella = removeSurcharge(price.umbrella);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        throw new PermissionsError(
          err.response.data.message,
          err.response.status
        );
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 404) {
        throw new NotFoundError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Unable to get price data. Please try again.",
          err.response.status
        );
      }
    }
    return price;
  }

  public async getPriceDoc(model: string): Promise<PriceRes> {
    let price;
    const url = "/prices/model/" + model;
    try {
      const res: AxiosResponse = await axios.get(url);
      price = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 403) {
        throw new PermissionsError(
          err.response.data.message,
          err.response.status
        );
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 404) {
        throw new NotFoundError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Unable to get price data. Please try again.",
          err.response.status
        );
      }
    }
    return price;
  }

  public async getBasePrice(body: {
    model: string;
    item_details: {
      base: BaseModel;
      baseFinish: FinishHandle;
      baseStem: Stem | null;
      baseOptions?: any;
    };
  }): Promise<PriceTotal> {
    let price;
    const url = "/prices/base";
    try {
      const res: AxiosResponse = await axios.post(url, body);
      price = res.data.data;
      if (price.base) {
        price.base = removeSurcharge(price.base);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        throw new PermissionsError(
          err.response.data.message,
          err.response.status
        );
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 404) {
        throw new NotFoundError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Unable to get price data. Please try again.",
          err.response.status
        );
      }
    }
    return price;
  }

  public async getQuickshipFiles(): Promise<QuickshipConfigFile[]> {
    let files;
    const url = "/quickship";
    try {
      const res: AxiosResponse = await axios.get(url);
      files = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 400) {
        throw new BadRequest(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 403) {
        throw new PermissionsError(
          err.response.data.message,
          err.response.status
        );
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 404) {
        throw new NotFoundError(
          "Configuration files not found.",
          err.response.status
        );
      } else {
        throw new APIError(
          "Something went wrong, please try again.",
          err.response.status
        );
      }
    }
    return files;
  }

  public async getCustomPrice(
    body: CustomUmbrella,
    quickship: boolean
  ): Promise<PriceTotal> {
    let price;
    let url = "/prices";
    if (quickship) {
      url += "?quickship=true";
    }
    try {
      const res: AxiosResponse = await axios.post(url, body);
      price = res.data.data;
    } catch (err) {
      if (err.response && err.response.status === 403) {
        throw new PermissionsError(
          err.response.data.message,
          err.response.status
        );
      } else if (err.response && err.response.status === 401) {
        throw new AuthError(err.response.data.message, err.response.status);
      } else if (err.response && err.response.status === 404) {
        throw new NotFoundError(err.response.data.message, err.response.status);
      } else {
        throw new APIError(
          "Unable to get price data. Please try again.",
          err.response.status
        );
      }
    }
    return price;
  }

  public async createFinishedUmbrellaSKU(data: ConfigFile): Promise<string> {
    let sku = "";
    const url = process.env.VUE_APP_API_NODE_URL + "/products/sku";

    try {
      const res: AxiosResponse = await axios.post(url, data);
      if (res.data) {
        sku = res.data.finishedUmbrellaSKU;
      }
    } catch (error) {
      sku = "Not Available...";
    }
    return sku;
  }

  public async createBaseSKU(data: ConfigFile): Promise<string> {
    let sku = "";
    const url = process.env.VUE_APP_API_NODE_URL + "/products/sku";

    try {
      const res: AxiosResponse = await axios.post(url, data);
      if (res.data) {
        sku = res.data.baseSKU;
      }
    } catch (error) {
      sku = "Not Available...";
    }
    return sku;
  }

  public async createChairSKU(data: {
    chair: {
      model: string;
      mainFabric: string;
      bindingFabric: string;
    };
  }): Promise<string> {
    let sku = "";
    const url = process.env.VUE_APP_API_NODE_URL + "/products/sku";

    try {
      const res: AxiosResponse = await axios.post(url, data);
      if (res.data) {
        sku = res.data.chairSKU;
      }
    } catch (error) {
      sku = "Not Available...";
    }
    return sku;
  }
}
